"use strict";
/**
 * Moderation utilities for Oracle chat systems.
 * Shared between OraclePage (Sage on the Stage) and OracleSideGuide (Guide on the Side).
 */
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.processModerationError = exports.isModerationError = exports.getUnknownModerationConfig = exports.getModerationConfig = exports.parseModerationCategories = void 0;
/**
 * Category to severity mapping.
 * Based on OpenAI moderation categories but designed to be provider-agnostic.
 */
var CATEGORY_SEVERITY_MAP = {
    // Critical - Distress (self-harm with intent/instructions)
    'self-harm/intent': { severity: 'CRITICAL_DISTRESS', showCrisisResources: true },
    'self-harm/instructions': { severity: 'CRITICAL_DISTRESS', showCrisisResources: true },
    // Critical - Legal (sexual content involving minors)
    'sexual/minors': { severity: 'CRITICAL_LEGAL', showCrisisResources: false },
    // Critical - Threat (threatening content)
    'hate/threatening': { severity: 'CRITICAL_THREAT', showCrisisResources: false },
    'harassment/threatening': { severity: 'CRITICAL_THREAT', showCrisisResources: false },
    // High severity
    'violence/graphic': { severity: 'HIGH', showCrisisResources: false },
    'self-harm': { severity: 'HIGH', showCrisisResources: true },
    // Medium severity
    hate: { severity: 'MEDIUM', showCrisisResources: false },
    harassment: { severity: 'MEDIUM', showCrisisResources: false },
    sexual: { severity: 'MEDIUM', showCrisisResources: false },
    violence: { severity: 'MEDIUM', showCrisisResources: false },
};
/**
 * Severity priority order (higher index = higher priority).
 * When multiple categories are flagged, use the highest severity.
 */
var SEVERITY_PRIORITY = [
    'MEDIUM',
    'HIGH',
    'CRITICAL_THREAT',
    'CRITICAL_LEGAL',
    'CRITICAL_DISTRESS',
];
/**
 * Known moderation error codes from the backend.
 * Extensible for other providers (Anthropic, etc.).
 */
var MODERATION_ERROR_CODES = new Set([
    'CONTENT_MODERATION', // OpenAI moderation
    // Future: add codes for other providers
]);
/**
 * Parse moderation categories from the backend error message.
 * Expected format: "Content flagged for: category1, category2, ..."
 *
 * @param message - The error message from the backend
 * @returns Array of category strings
 */
var parseModerationCategories = function (message) {
    var prefix = 'Content flagged for:';
    var prefixIndex = message.indexOf(prefix);
    if (prefixIndex === -1) {
        return [];
    }
    var categoriesStr = message.slice(prefixIndex + prefix.length).trim();
    if (!categoriesStr) {
        return [];
    }
    return categoriesStr
        .split(',')
        .map(function (cat) { return cat.trim(); })
        .filter(function (cat) { return cat.length > 0; });
};
exports.parseModerationCategories = parseModerationCategories;
/**
 * Get the moderation configuration for a set of flagged categories.
 * Returns the highest severity config based on priority.
 *
 * @param categories - Array of flagged category strings
 * @returns ModerationConfig with severity, crisis resources flag, and i18n key
 */
var getModerationConfig = function (categories) {
    if (categories.length === 0) {
        return (0, exports.getUnknownModerationConfig)();
    }
    // Find highest severity and determine if crisis resources should be shown
    var result = categories.reduce(function (acc, category) {
        var config = CATEGORY_SEVERITY_MAP[category];
        if (config) {
            var severityIndex = SEVERITY_PRIORITY.indexOf(config.severity);
            if (severityIndex > acc.highestSeverityIndex) {
                return {
                    highestSeverityIndex: severityIndex,
                    matchedSeverity: config.severity,
                    // Show crisis resources if any flagged category requires it
                    showCrisisResources: acc.showCrisisResources || config.showCrisisResources,
                };
            }
            // Even if this isn't highest severity, still check for crisis resources
            if (config.showCrisisResources && !acc.showCrisisResources) {
                return __assign(__assign({}, acc), { showCrisisResources: true });
            }
        }
        return acc;
    }, {
        highestSeverityIndex: -1,
        showCrisisResources: false,
        matchedSeverity: 'UNKNOWN',
    });
    // If no categories matched, return UNKNOWN
    if (result.highestSeverityIndex === -1) {
        return (0, exports.getUnknownModerationConfig)();
    }
    return {
        severity: result.matchedSeverity,
        showCrisisResources: result.showCrisisResources,
        i18nKey: result.matchedSeverity,
    };
};
exports.getModerationConfig = getModerationConfig;
/**
 * Get the default config for unknown/unrecognized moderation errors.
 * Used when:
 * - Categories cannot be parsed from the message
 * - Categories don't match any known patterns
 * - Error comes from a different provider with unknown format
 */
var getUnknownModerationConfig = function () { return ({
    severity: 'UNKNOWN',
    showCrisisResources: false,
    i18nKey: 'UNKNOWN',
}); };
exports.getUnknownModerationConfig = getUnknownModerationConfig;
/**
 * Check if an error code indicates a moderation error.
 * Extensible for other LLM providers.
 *
 * @param code - The error code from errorInfo
 * @returns true if this is a moderation-related error
 */
var isModerationError = function (code) { return MODERATION_ERROR_CODES.has(code); };
exports.isModerationError = isModerationError;
/**
 * Process errorInfo from a subscription payload and extract moderation error if present.
 *
 * @param errorInfo - Array of error objects from GraphQL payload
 * @returns ModerationError if moderation error detected, null otherwise
 */
var processModerationError = function (errorInfo) {
    if (!errorInfo || errorInfo.length === 0) {
        return null;
    }
    var firstError = errorInfo[0];
    if (!(0, exports.isModerationError)(firstError.code)) {
        return null;
    }
    var categories = (0, exports.parseModerationCategories)(firstError.message);
    var config = (0, exports.getModerationConfig)(categories);
    return {
        categories: categories,
        config: config,
    };
};
exports.processModerationError = processModerationError;
